import ReactMarkdown from 'react-markdown';

import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-light-svg-icons';
import { faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import remarkGfm from 'remark-gfm';

import { LoadingDots } from './LoadingDots';

const REMARK_PLUGINS = [remarkGfm];

const MARKDOWN_COMPONENTS = {
    table: (props) => (
        <div className='mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm'>
            <table
                className='min-w-full border-separate border-spacing-0 text-[12px] leading-5'
                {...props}
            />
        </div>
    ),
    th: (props) => (
        <th
            className='sticky top-0 z-10 border-b border-slate-200 bg-slate-100 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700 first:rounded-tl-xl last:rounded-tr-xl'
            {...props}
        />
    ),
    tr: (props) => (
        <tr
            className='odd:bg-white even:bg-slate-50/70 hover:bg-slate-100/70'
            {...props}
        />
    ),
    td: (props) => (
        <td
            className='border-b border-slate-100 px-3 py-2 align-top text-slate-700'
            {...props}
        />
    ),
    a: ({ href, children, ...props }) => (
        <a
            href={href}
            className='font-medium text-blue-700 underline underline-offset-2 transition-colors hover:text-blue-800'
            target='_blank'
            rel='noopener noreferrer'
            {...props}
        >
            {children}
        </a>
    ),
    code: ({ inline, children, ...props }) =>
        inline ? (
            <code
                className='rounded-[3px] bg-gray-200 px-1 py-px text-xs'
                {...props}
            >
                {children}
            </code>
        ) : (
            <pre className='overflow-x-auto rounded-md bg-gray-200 p-2 text-xs'>
                <code {...props}>{children}</code>
            </pre>
        ),
};

export const AiChatThread = ({
    messages,
    isLoading,
    loadingStatus,
    ratings,
    onRate,
    messagesEndRef,
    onConfirmSql,
    t,
    userInitials,
}) => {
    return (
        <div className='flex min-h-0 flex-col gap-3 px-4 pb-2 pt-4'>
            {messages.map((msg, i) => (
                <div
                    key={i}
                    className={clsx(
                        'flex max-w-[88%] items-start gap-2',
                        msg.role === 'user' ? 'self-end' : 'self-start'
                    )}
                >
                    {msg.role === 'assistant' ? (
                        <div className='mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-lime-200'>
                            <FontAwesomeIcon
                                icon={faWandMagicSparkles}
                                className='text-xs'
                            />
                        </div>
                    ) : null}
                    <div className='flex min-w-0 flex-col gap-1.5'>
                        <div
                            className={clsx(
                                'px-3 py-2.5 text-[13px] leading-[1.5] min-w-0',
                                msg.role === 'user'
                                    ? 'rounded-2xl rounded-tr-sm bg-slate-900 text-slate-50'
                                    : 'rounded-2xl rounded-tl-sm border border-gray-200 bg-white text-slate-900 shadow-[0_4px_14px_rgba(15,23,42,0.05)]'
                            )}
                        >
                            {msg.role === 'user' ? (
                                msg.content
                            ) : (
                                <ReactMarkdown
                                    remarkPlugins={REMARK_PLUGINS}
                                    components={MARKDOWN_COMPONENTS}
                                >
                                    {msg.content}
                                </ReactMarkdown>
                            )}
                        </div>
                        {msg.role === 'assistant' && msg.sql && (
                            <div className='flex gap-1.5'>
                                {['up', 'down'].map((dir) => (
                                    <button
                                        key={dir}
                                        type='button'
                                        onClick={() => onRate(i, dir)}
                                        disabled={ratings[i] != null}
                                        title={dir === 'up' ? t('Good answer') : t('Bad answer')}
                                        className={clsx(
                                            'rounded-lg border px-2 py-1 text-[13px] leading-none transition-all',
                                            ratings[i] === dir
                                                ? dir === 'up'
                                                    ? 'border-green-300 bg-green-100'
                                                    : 'border-red-300 bg-red-100'
                                                : 'border-gray-200 bg-gray-50',
                                            ratings[i] != null ? 'cursor-default' : 'cursor-pointer',
                                            ratings[i] != null && ratings[i] !== dir ? 'opacity-45' : 'opacity-100'
                                        )}
                                    >
                                        {dir === 'up' ? (
                                            <FontAwesomeIcon
                                                icon={faThumbsUp}
                                                className='text-lime-500'
                                            />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faThumbsDown}
                                                className='text-red-500'
                                            />
                                        )}
                                    </button>
                                ))}
                                {msg.requiresConfirmation && (
                                    <button
                                        type='button'
                                        onClick={() => onConfirmSql(msg.sql)}
                                        className='cursor-pointer rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700'
                                    >
                                        {t('Confirm')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {msg.role === 'user' ? (
                        <div className='mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold uppercase text-white'>
                            {userInitials}
                        </div>
                    ) : null}
                </div>
            ))}
            {isLoading && (
                <div className='flex self-start rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 shadow-[0_4px_14px_rgba(15,23,42,0.05)]'>
                    <LoadingDots />
                    <span className='ml-2'>
                        {loadingStatus === 'thinking' && t('Thinking...')}
                        {loadingStatus === 'running' && t('Running...')}
                        {loadingStatus === 'prettifying' && t('Formatting results...')}
                    </span>
                </div>
            )}
            <div
                ref={messagesEndRef}
                className='h-2 shrink-0'
            />
        </div>
    );
};
